import { useEffect, useState } from "react";
import {
  Icon,
  IconButton,
  Paper,
  TableRow,
  TableCell,
  useTheme,
  Modal,
  Grid,
  TextField,
  MenuItem,
  Button,
  Typography,
} from "@mui/material";
import { FerramanetasDaListagem } from "../../shared/components/ferramentasDaListagem/ferramentas-listagem";
import { BaseLayout } from "../../shared/layouts/base-layout-page";
import { ENVIRONMENT } from "../../environment/environment";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { PaginacaoListagem } from "../../shared/components/ferramentasDaListagem/paginacao-listagem";
import { useDebounce } from "../../shared/utils/useDebounce/use-debounce";
import { toastError } from "../../shared/services/errors/erros-handler";
import { orcamentoService } from "./service/orcamento.service";
import { TabelaDaListagem } from "../../shared/components/tabela-da-listagem/tabelaDalistagem";
import { formatarCpfCnpj, toSlug } from "../../shared/utils";
import { useLoader } from "../../shared/contexts/LoaderContext";
import { set, useForm } from "react-hook-form";
import { AprovarOrcamentoModal } from './components/ApproveBudgetModal';
import { ModalCancelamento } from "../../shared/components/ModalCancelar/modal-cancelar";
import { yupResolver } from "@hookform/resolvers/yup";
import { useMutation } from "@tanstack/react-query";
import { CancelarOrcamentoFormData, cancelarOrcamentoSchema } from "./schema/cancelar-orcamento.schema.ts";

// Enum traduzido para status do orçamento
export enum StatusBudgetEnum {
  GENERATED = 'generated',
  APPROVED = 'approved',
  REJECTED = 'rejected',
  CANCELED = 'canceled',
  EXPIRED = 'expired',
}

// Função para traduzir status
const statusBudgetLabel: Record<string, string> = {
  [StatusBudgetEnum.GENERATED]: 'Gerado',
  [StatusBudgetEnum.APPROVED]: 'Aprovado',
  [StatusBudgetEnum.REJECTED]: 'Rejeitado',
  [StatusBudgetEnum.CANCELED]: 'Cancelado',
  [StatusBudgetEnum.EXPIRED]: 'Expirado',
};

// Função para definir cor do status
const statusBudgetColor: Record<string, string> = {
  [StatusBudgetEnum.GENERATED]: '#1976d2', // azul
  [StatusBudgetEnum.APPROVED]: '#388e3c', // verde
  [StatusBudgetEnum.REJECTED]: '#d32f2f', // vermelho
  [StatusBudgetEnum.CANCELED]: '#757575', // cinza
  [StatusBudgetEnum.EXPIRED]: '#ffa000', // amarelo
};

function ModalFiltrosOrcamentos({
  open,
  onClose,
  onFiltrar,
  filtrosIniciais,
}: {
  open: boolean;
  onClose: () => void;
  onFiltrar: (filtros: { status: string[]; nome: string }) => void;
  filtrosIniciais: { status: string[]; nome: string };
}) {
  const [status, setStatus] = useState<string[]>(filtrosIniciais.status);
  const [nome, setNome] = useState(filtrosIniciais.nome);

  useEffect(() => {
    if (open) {
      setStatus(filtrosIniciais.status);
      setNome(filtrosIniciais.nome);
    }
  }, [open, filtrosIniciais]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onFiltrar({ status, nome });
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Paper
        sx={{
          width: { xs: "92%", sm: 420 },
          maxWidth: "96vw",
          margin: "80px auto",
          padding: 3,
          outline: "none",
        }}
      >
        <Typography variant="h6" mb={2}>
          Filtros de Orçamentos
        </Typography>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                label="Nome do cliente ou do orçamento"
                value={nome}
                onChange={(e) => setNome(e.target.value)}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Status"
                select
                value={status}
                onChange={(e) =>
                  setStatus(
                    Array.isArray(e.target.value)
                      ? e.target.value
                      : [e.target.value]
                  )
                }
                fullWidth
                SelectProps={{ multiple: true }}
                InputLabelProps={{ shrink: true }}
              >
                {Object.values(StatusBudgetEnum).map((value) => (
                  <MenuItem key={value} value={value}>
                    {statusBudgetLabel[value]}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} display="flex" justifyContent="flex-end" gap={1}>
              <Button variant="outlined" onClick={onClose}>
                Cancelar
              </Button>
              <Button variant="contained" type="submit">
                Filtrar
              </Button>
            </Grid>
          </Grid>
        </form>
      </Paper>
    </Modal>
  );
}

export function ListarOrcamentos() {
  const [orcamentos, setOrcamentos] = useState<any[]>([]);
  const [busca, setBusca] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [query, setQuery] = useState<string | undefined>(undefined);
  const [filtros, setFiltros] = useState<{ status: string[]; nome: string }>({ status: [], nome: "" });
  const [openFiltros, setOpenFiltros] = useState(false);
  const [openApproveModal, setOpenApproveModal] = useState(false);
  const [selectedOrcamentoId, setSelectedOrcamentoId] = useState<string | null>(null);

  const buscaDebounce = useDebounce(query || "", 500);
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const reload = location.state?.reload;
  const { showLoader, hideLoader } = useLoader();
  const [openModalCancel, setOpenModalCancel] = useState(false);
  const [openModalDetalhe, setOpenModalDetalhe] = useState(false);
  const [idOrcamento, setIdOrcamento] = useState<string>("");

  useEffect(() => {
    const normalizarDocumento = (valor: string) => valor.replace(/[^\d]/g, "");

    const gerarQuery = () => {
      const partes: string[] = [];

      if (busca && busca.trim() !== "") {
        const valorLimpo = normalizarDocumento(busca);
        if (valorLimpo) {
          const campo = valorLimpo.length <= 11 ? "cpf" : "cnpj";
          partes.push(`campo_pesquisa=${campo}&tipo_pesquisa=comeca&valor_pesquisa=${valorLimpo}`);
        }
      }

      if (filtros.nome && filtros.nome.trim() !== "") {
        partes.push(`campo_pesquisa=slug&tipo_pesquisa=comeca&valor_pesquisa=${toSlug(filtros.nome)}`);
      }

      if (filtros.status.length > 0) {
        partes.push(`campo_pesquisa=status&tipo_pesquisa=in&valor_pesquisa=${filtros.status.join(",")}`);
      }

      return partes.length > 0 ? partes.join("&") : undefined;
    };

    setQuery(gerarQuery());
  }, [busca, filtros]);

  const carregarOrcamentos = async (
    query?: string,
    pagina = page,
    limite = rowsPerPage
  ) => {
    try {
      showLoader();
      const result = await orcamentoService.findAll(query, pagina, limite);
      setOrcamentos(result.items || []);
      setTotalCount(result?.meta?.totalItems || 0);
      setIsLoading(false);
      setTimeout(() => { hideLoader(); }, 1000);
    } catch (error) {
      hideLoader();
      console.error("Erro ao buscar orçamentos:", error);
      setOrcamentos([]);
    }
  };

  useEffect(() => {
    if (reload) {
      setIsLoading(true);
      carregarOrcamentos(undefined, 0);
    }
  }, [reload]);

  useEffect(() => {
    setPage(0);
    carregarOrcamentos(buscaDebounce, 0);
  }, [buscaDebounce]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
    carregarOrcamentos(buscaDebounce, newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newLimit = parseInt(event.target.value, 10);
    setRowsPerPage(newLimit);
    setPage(0);
    carregarOrcamentos(buscaDebounce, 0, newLimit);
  };

  const handleDelete = async (id: string) => {
    if (window.confirm(`Tem certeza que deseja excluir o orçamento?`)) {
      try {
        await orcamentoService.deletar(id);
        toast.success(`Orçamento excluído com sucesso!`);
        carregarOrcamentos(buscaDebounce, page, rowsPerPage);
      } catch (error) {
        toastError(error);
      }
    }
  };


  const handleOpenApproveModal = (id: string) => {
    if (id) {
      setSelectedOrcamentoId(id);
      setOpenApproveModal(true);
    }
  };


  const handleCancel = (idOrcamentoParam: string) => {
    if (idOrcamentoParam && idOrcamentoParam.trim() !== "") {
      setIdOrcamento(idOrcamentoParam);
      setOpenModalCancel(true);
    }
  };


  const handleCloseApproveModal = () => {
    setOpenApproveModal(false);
    setSelectedOrcamentoId(null);
  };

  const methods = useForm<CancelarOrcamentoFormData>({
    resolver: yupResolver(cancelarOrcamentoSchema),
  });

  const { isPending, mutate } = useMutation({
    mutationFn: async (data: CancelarOrcamentoFormData) => {
      await orcamentoService.cancelar(idOrcamento, data);
    },
    onSuccess: () => {
      toast.success("Orçamento cancelado com sucesso!");
      setOpenModalCancel(false);
    },
    onError: (err: any) => {
      toastError(err);
    },
    onMutate: () => {
      showLoader();
    },
    onSettled: () => {
      hideLoader();
    },
  });

  return (
    <BaseLayout
      title="Orçamentos"
      barraDeFerramentas={
        <FerramanetasDaListagem
          placeholder={ENVIRONMENT.INPUT_DE_BUSCA + " por CPF ou CNPJ"}
          textoBotaoNovo="Adicionar Novo"
          mostrarInputBusca
          textoDaBusca={busca}
          aoMudarTextoDeBusca={setBusca}
          aoClicarBotaoNovo={() => navigate("/orcamento/criar")}
          mostraBotaoFiltro
          textoBotaoFiltro="Filtros Avançados"
          aoClicarBotaoFiltro={() => setOpenFiltros(true)}
        />
      }
    >

      <ModalFiltrosOrcamentos
        open={openFiltros}
        onClose={() => setOpenFiltros(false)}
        filtrosIniciais={filtros}
        onFiltrar={(novosFiltros) => {
          setFiltros(novosFiltros);
          setPage(0);
          setOpenFiltros(false);
        }}
      />
      <ModalCancelamento
        open={openModalCancel}
        onClose={() => setOpenModalCancel(false)}
        onSubmit={(formData) => mutate(formData)}
        methods={methods}
        isPending={isPending}
      />
      <Paper
        sx={{
          width: "100%",
          overflow: "hidden",
          border: "1px solid #ccc",
          borderRadius: 2,
        }}
      >
        <TabelaDaListagem
          ariaLabel="tabela de orcamentos"
          headTable={[
            "Descrição",
            "Nome",
            "CPF/CNPJ",
            "Telefone",
            "Status",
            "Validadade",
            "Valor Total",
            "Ações",
          ]}
          tableBody={
            orcamentos.length > 0 ? (
              orcamentos.map((orcamento) => (
                <TableRow hover key={orcamento.id}>
                  <TableCell>{orcamento.description}</TableCell>
                  <TableCell>{orcamento.fullName || orcamento.corporateName || orcamento.fantasyName}</TableCell>
                  <TableCell>{formatarCpfCnpj(orcamento.cpf) || formatarCpfCnpj(orcamento.cnpj)}</TableCell>
                  <TableCell>{orcamento.phone}</TableCell>
                  <TableCell>
                    <span
                      style={{
                        color: statusBudgetColor[orcamento.status] || '#333',
                        fontWeight: 'bold',
                        padding: '2px 8px',
                        borderRadius: '12px',
                        background: '#f5f5f5',
                        display: 'inline-block',
                      }}
                    >
                      {statusBudgetLabel[orcamento.status] || orcamento.status}
                    </span>
                  </TableCell>
                  <TableCell>{new Date(orcamento.dateValidation).toLocaleDateString('pt-BR')}</TableCell>
                  <TableCell>{orcamento.totalValue?.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</TableCell>
                  <TableCell>
                    {
                      orcamento.status === StatusBudgetEnum.APPROVED ? (
                        <IconButton
                          size="small"
                          onClick={() => navigate(`/orcamento/detalhe/${orcamento.id}`)}
                        >
                          <Icon>visibility</Icon>
                        </IconButton>
                      ) : (
                        <IconButton
                          size="small"
                          onClick={() => navigate(`/orcamento/detalhe/${orcamento.id}`)}
                        >
                          <Icon>edit</Icon>
                        </IconButton>
                      )
                    }
                    <IconButton
                      size="small"
                      onClick={() => handleDelete(orcamento.id)}
                    >
                      <Icon>delete</Icon>
                    </IconButton>
                    {orcamento.status === StatusBudgetEnum.GENERATED && (
                      <IconButton
                        size="small"
                        color="success"
                        onClick={() => handleOpenApproveModal(orcamento.id)}
                      >
                        <Icon>check_circle</Icon>
                      </IconButton>
                    )}
                    {orcamento.status === StatusBudgetEnum.APPROVED && (
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleCancel(orcamento.id)}
                      >
                        <Icon>cancel</Icon>
                      </IconButton>
                    )}
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => {
                        if (window.confirm("Deseja duplicar este orçamento?")) {
                          orcamentoService.duplicarOrcamento(orcamento.id)
                            .then(() => {
                              toast.success("Orçamento duplicado com sucesso!");
                              carregarOrcamentos(buscaDebounce, page, rowsPerPage);
                            })
                            .catch((error) => {
                              toastError(error);
                            });
                        }
                      }}
                    >
                      <Icon>content_copy</Icon>
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  Nenhum orçamento encontrado.
                </TableCell>
              </TableRow>
            )
          }
        />
        <PaginacaoListagem
          totalCount={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          handleChangePage={handleChangePage}
          handleChangeRowsPerPage={handleChangeRowsPerPage}
        />
        {selectedOrcamentoId && (
          <AprovarOrcamentoModal
            open={openApproveModal}
            idOrcamento={selectedOrcamentoId}
            onClose={handleCloseApproveModal}
            onSubmit={() => { }}
          />
        )}
      </Paper>
    </BaseLayout>
  );
}
